const { BROKER_CONNECTIONS_ROUTE, SITE_BASE_URL } = require('./brokerConnectionSource');
const { publicPositioningPages } = require('./comparisonContent');
const { corePublicPageContent } = require('./corePublicPageContent');
const { ABOUT_VISIBLE, ABOUT_MORE } = require('./courseOverview');
const { trustPageContent } = require('./trustPageContent');
const { ROB_GRZESIK_AUTHOR } = require('./editorialContent');
const {
  PUBLIC_ROUTE_REDIRECT_DEFAULT_QUERIES,
  withRedirectDefaultQuery,
} = require('./redirectDefaultQueries');

const EDUCATION_CANONICAL_PATHS = Object.freeze({
  bootcamp: '/courses/ai-trading-bootcamp',
  selfPaced: '/courses/ai-trading-bootcamp/self-paced',
});

const EDUCATION_REDIRECTS = Object.freeze({
  '/ai-bot-builder-bootcamp': EDUCATION_CANONICAL_PATHS.bootcamp,
  '/ai-bot-builder-bootcamp/sales': EDUCATION_CANONICAL_PATHS.bootcamp,
  '/ai-bot-builder-bootcamp/self-paced': EDUCATION_CANONICAL_PATHS.selfPaced,
  '/ai-trading-course': EDUCATION_CANONICAL_PATHS.bootcamp,
  '/courses/ai-trading': EDUCATION_CANONICAL_PATHS.bootcamp,
  '/courses/ai-investing': EDUCATION_CANONICAL_PATHS.bootcamp,
  // Vanity links spoken in videos ("botspot.trade/youtube", "/membership").
  '/membership': EDUCATION_CANONICAL_PATHS.selfPaced,
  '/youtube': EDUCATION_CANONICAL_PATHS.selfPaced,
  // "botspot.trade/ytmembership" is the link read aloud in YouTube videos.
  '/ytmembership': EDUCATION_CANONICAL_PATHS.selfPaced,
  '/yt-membership': EDUCATION_CANONICAL_PATHS.selfPaced,
});

const PUBLIC_ROUTE_REDIRECTS = Object.freeze({
  ...EDUCATION_REDIRECTS,
  '/developers': '/agents',
  '/case-studies/complex-backtests': '/case-studies',
  '/sample-complex-backtest': '/case-studies',
});

const EDUCATION_REDIRECT_ALIAS_PATHS = Object.freeze(Object.keys(EDUCATION_REDIRECTS));

const CORE_PUBLIC_SITE_ROUTES = [
  {
    path: '/',
    title: 'AI Trading Bot Builder & Backtesting Platform | BotSpot',
    description:
      'Turn your trading rules into a testable system. Build with AI, inspect historical evidence, and connect your broker with BotSpot.',
    content: corePublicPageContent('/'),
  },
  {
    path: '/sales',
    title: 'Build AI Trading Bots in Minutes | BotSpot',
    description:
      'Build, backtest, and deploy automated trading strategies with AI. Start without coding and connect your existing brokerage account.',
    content: corePublicPageContent('/sales'),
  },
  {
    path: '/marketplace',
    title: 'Algorithm Marketplace: AI Trading Strategies | BotSpot',
    description:
      'Browse public trading strategies with live history and backtest results, then clone a strategy and customize it in BotSpot.',
    content: corePublicPageContent('/marketplace'),
  },
  {
    path: '/sales-marketplace',
    title: 'Find an Automated Trading Strategy | BotSpot',
    description:
      'Explore ready-to-run trading strategies, review performance data, and choose a bot to customize and deploy through BotSpot.',
  },
  {
    path: '/pricing',
    title: 'AI Trading Bot Pricing & Plans | BotSpot',
    description:
      'Compare BotSpot plans for AI strategy building, backtesting, live bots, broker connections, support, and trading education.',
    content: corePublicPageContent('/pricing'),
  },
  {
    path: '/enterprise',
    title: 'AI Solutions for Investment Firms | BotSpot',
    description:
      'Diagnostic-led AI implementation for investment-firm growth, research, and senior-team capacity.',
  },
  {
    path: '/agents',
    title: 'BotSpot MCP for Agents | ChatGPT, Claude, Codex & Gemini',
    description:
      'Connect BotSpot to ChatGPT, Claude, Claude Code, Codex, Gemini CLI, Cursor, and other MCP clients for AI trading workflows.',
    content: {
      eyebrow: 'BotSpot MCP',
      heading: 'MCP for Agents',
      summary:
        'Use BotSpot from ChatGPT, Claude, Claude Code, Codex, Gemini CLI, Cursor, or another MCP-compatible client.',
      sections: [
        {
          heading: 'Visible setup guides for every supported client',
          paragraphs: [
            'Each client guide is published directly in the page instead of being hidden behind an interactive tab.',
          ],
          bullets: [
            'ChatGPT connects to BotSpot through an OAuth app.',
            'Claude connects to BotSpot through a remote OAuth MCP connector.',
            'Claude Code connects to BotSpot with a local API key.',
            'Codex connects to BotSpot through its MCP configuration.',
            'Gemini CLI connects to BotSpot through Streamable HTTP and OAuth.',
            'Cursor connects to BotSpot through its MCP configuration.',
          ],
        },
      ],
      relatedLinks: [
        { label: 'Create a BotSpot API key', url: '/account-settings/api-keys' },
        { label: 'Open the BotSpot AI Agent', url: '/ai-agent' },
      ],
    },
  },
  {
    path: '/case-studies',
    lastmod: '2026-10-02',
    title: 'Algorithmic Trading Case Studies | BotSpot',
    description:
      'Explore complex backtests and practical examples of AI-assisted algorithmic trading workflows built with BotSpot.',
  },
  {
    path: EDUCATION_CANONICAL_PATHS.bootcamp,
    title: 'AI Trading Course & Live Bootcamp | BotSpot',
    description:
      'Learn AI trading with Claude, Codex and BotSpot in live sessions with Rob Grzesik. Build research workflows and an AI trading agent. Backed by a 30-day money-back guarantee.',
    content: {
      heading: 'AI Trading Bootcamp',
      summary: 'Learn AI-assisted trading in a live, hands-on course with Rob Grzesik.',
      sections: [{ heading: 'About this bootcamp', paragraphs: [...ABOUT_VISIBLE, ...ABOUT_MORE] }],
      relatedLinks: [
        {
          label: 'See the AI Trading Bootcamp Membership',
          url: EDUCATION_CANONICAL_PATHS.selfPaced,
        },
      ],
    },
  },
  {
    path: EDUCATION_CANONICAL_PATHS.selfPaced,
    title: 'AI Trading Bootcamp Membership | BotSpot',
    description:
      'A year of AI that researches and trades for you with real money. Every AI Trading Bootcamp recording, 12 months of BotSpot Pro and live Tuesday Sessions with Rob. Backed by a 30-day money-back guarantee.',
  },
  {
    path: '/challenges',
    title: 'Free AI Trading Challenges | BotSpot',
    description:
      'Join free live BotSpot AI trading challenges, build one practical workflow with Rob, and review your work before choosing the full bootcamp.',
    content: {
      eyebrow: 'Free live education',
      heading: 'Free AI Trading Challenges',
      summary:
        'Build one useful AI trading workflow at one live show with Rob, then keep improving it at your own pace over seven days.',
      sections: [
        {
          heading: 'How each challenge works',
          paragraphs: [
            'Current public challenge dates and registration come from BotSpot live session data. There is one challenge page; it always shows the current run.',
          ],
          bullets: [
            'Join the live show with Rob.',
            'Improve one practical project during the week.',
            'Have one paper or real trade ready by the end of the week.',
          ],
        },
      ],
      relatedLinks: [
        { label: 'Explore the AI Trading Bootcamp', url: EDUCATION_CANONICAL_PATHS.bootcamp },
      ],
    },
  },
  {
    path: '/live-algo-lab',
    title: 'Live Algorithmic Trading Lab | BotSpot',
    description:
      'Join live weekly sessions covering real BotSpot workflows, algorithmic trading builds, strategy reviews, and implementation Q&A.',
  },
  {
    path: '/upgrade-to-pro',
    title: 'BotSpot Pro: More Bots, Backtests & Support',
    description:
      'Upgrade BotSpot for higher strategy revision limits, more live bots, additional backtesting capacity, and priority support.',
  },
  {
    path: '/help',
    title: 'BotSpot Help & Support',
    description:
      'Find BotSpot setup guidance, product help, broker connection information, and support resources.',
  },
  {
    path: '/about',
    lastmod: '2026-10-02',
    title: 'About BotSpot | AI Trading Platform by Lumiwealth',
    description:
      'Learn what BotSpot does, who operates it, how customer control works, and what its trading software does not promise.',
    content: trustPageContent('about'),
  },
  {
    path: '/contact',
    title: 'Contact BotSpot | Product Support and Business Inquiries',
    description:
      'Contact BotSpot for product support, account questions, MCP integrations, partnerships, enterprise services, and general inquiries.',
    content: trustPageContent('contact'),
  },
  {
    path: '/terms',
    title: 'Terms of Service | BotSpot',
    description:
      'Read BotSpot terms covering platform use, broker connections, marketplace content, AI outputs, fees, and trading risk.',
  },
  {
    path: '/privacy',
    title: 'Privacy Policy | BotSpot',
    description:
      'Read how BotSpot collects, uses, protects, and shares information when you use its websites, applications, and services.',
  },
  {
    path: '/blog',
    title: 'AI Trading & Market Automation Blog | BotSpot',
    description:
      'Read BotSpot market newsletters, automated trading tutorials, AI strategy guides, and product updates.',
    content: corePublicPageContent('/blog'),
  },
  {
    path: '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds',
    title: 'Build and Backtest an AI Swing Trading Bot | BotSpot',
    description:
      'A historical walkthrough of AI-generated swing-trading rules, simulated backtests, and broker deployment, with current BotSpot workflow guides.',
    pageType: 'article',
    author: ROB_GRZESIK_AUTHOR,
    publishedAt: '2025-02-05',
    modifiedAt: '2026-10-02',
    lastmod: '2026-10-02',
  },
];

const PUBLIC_SITE_ROUTES = [...CORE_PUBLIC_SITE_ROUTES, ...publicPositioningPages];

const APP_SITE_ROUTES = [
  {
    path: BROKER_CONNECTIONS_ROUTE,
    title: 'Broker Connections',
    description:
      'Authenticated BotSpot page for connecting, reconnecting, and managing broker credentials.',
    authenticated: true,
    sitemap: false,
    llms: true,
    robots: 'noindex, follow',
  },
  {
    path: '/ai-agent',
    title: 'BotSpot AI Agent',
    description:
      'Authenticated AI agent workspace for strategy research, building, backtesting, and broker-aware answers.',
    authenticated: true,
    sitemap: false,
    llms: true,
    robots: 'noindex, follow',
  },
  {
    path: '/dashboard',
    title: 'BotSpot Dashboard',
    description: 'Authenticated BotSpot dashboard for bots, backtests, and account activity.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/my-bots',
    title: 'My Bots',
    description: 'Authenticated list of trading bots in a BotSpot account.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/login',
    title: 'Sign In to BotSpot',
    description: 'Authenticated BotSpot sign-in page.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/account-settings',
    title: 'Account Settings',
    description: 'Authenticated BotSpot account settings.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/add-bot',
    title: 'Add Bot',
    description: 'Authenticated BotSpot flow for adding a bot.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/backtest',
    title: 'Run Backtest',
    description: 'Authenticated BotSpot backtest workspace.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/deploy',
    title: 'Deploy Bot',
    description: 'Authenticated BotSpot deployment workspace.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/brokerage-accounts',
    title: 'Brokerage Accounts',
    description: 'Authenticated BotSpot brokerage account list.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/my-backtests',
    title: 'My Backtests',
    description: 'Authenticated BotSpot backtest history.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/my-deployments',
    title: 'My Deployments',
    description: 'Authenticated BotSpot deployment list.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/my-strategies',
    title: 'My Strategies',
    description: 'Authenticated BotSpot strategy list.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/my-published-strategies',
    title: 'My Published Strategies',
    description: 'Authenticated BotSpot published-strategy list.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/user_profile',
    title: 'User Profile',
    description: 'Authenticated BotSpot profile page.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
  {
    path: '/affiliate/dashboard',
    title: 'Affiliate Dashboard',
    description: 'Authenticated BotSpot affiliate dashboard.',
    authenticated: true,
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
  },
];

const PUBLIC_NOINDEX_SITE_ROUTES = [
  ...[
    '/courses-clarity',
    '/courses-clarity/ai-trading-bootcamp',
    '/courses/ai-trading-bootcamp/control',
    '/courses/ai-trading-bootcamp/agentic',
    '/courses/ai-trading-bootcamp/v2',
    '/courses/ai-trading-bootcamp/clarity-home',
  ].map((path) => ({
    path,
    title: 'Bootcamp page review | BotSpot',
    description: 'Course page review.',
    robots: 'noindex, follow',
    sitemap: false,
  })),
  // The two static challenge steps (Rob, 2026-09-11: no per-run URLs). Both are
  // noindex in React. They still need their OWN crawler document: without one,
  // the S3 origin answered with the homepage document and a homepage canonical,
  // which is what Google's ad crawler read for the call ad landing on
  // /challenges/welcome.
  {
    path: '/challenges/welcome',
    title: 'Your AI Trading Challenge | BotSpot',
    description:
      'Book a free setup call for the free live AI Trading Challenge, or save your free seat for the next kickoff.',
    robots: 'noindex, follow',
    sitemap: false,
    llms: false,
  },
  {
    path: '/challenges/call-confirmed',
    title: 'Your call details | BotSpot',
    description: 'Your free AI Trading Challenge setup call is booked.',
    robots: 'noindex, follow',
    sitemap: false,
    llms: false,
  },
  {
    path: '/lp/google-search',
    title: 'BotSpot Google Search Landing Page',
    description:
      'Paid search landing page for BotSpot. This URL is not a public marketing index page.',
    sitemap: false,
    llms: false,
    robots: 'noindex, follow',
    content: {
      eyebrow: 'Paid landing page',
      heading: 'BotSpot Google Search landing page',
      summary:
        'This paid search landing page is kept out of the public sitemap and is marked noindex in raw HTML.',
      sections: [
        {
          heading: 'Not a public catalog page',
          paragraphs: [
            'Use the BotSpot homepage, pricing page, marketplace, blog, compare pages, and guides for public marketing content. This landing page exists for paid traffic and should not inherit homepage SEO.',
          ],
        },
      ],
    },
  },
];

const NOINDEX_SITE_ROUTES = [...PUBLIC_NOINDEX_SITE_ROUTES, ...APP_SITE_ROUTES];

module.exports = {
  APP_SITE_ROUTES,
  CORE_PUBLIC_SITE_ROUTES,
  EDUCATION_CANONICAL_PATHS,
  EDUCATION_REDIRECT_ALIAS_PATHS,
  EDUCATION_REDIRECTS,
  NOINDEX_SITE_ROUTES,
  PUBLIC_ROUTE_REDIRECT_DEFAULT_QUERIES,
  PUBLIC_NOINDEX_SITE_ROUTES,
  PUBLIC_SITE_ROUTES,
  PUBLIC_ROUTE_REDIRECTS,
  SITE_BASE_URL,
  withRedirectDefaultQuery,
};
